import {useEffect, type RefObject} from "react";
import {useMotionValue, type MotionValue} from "motion/react";

/**
 * Прогресс прохода блока через экран: 0 — верх блока у верха экрана, 1 — блок
 * целиком ушёл вверх. Нужен параллаксу первого экрана.
 *
 * Почему не `useScroll` из motion/react: лендинг скроллится не окном, а внутри
 * `ScrollArea` (OverlayScrollbars, см. `pages/onboarding/LandingPage.tsx`), и
 * `useScroll` без ссылки на именно этот контейнер ничего не слышит. Слушаем
 * прокрутку на перехвате, как `useScrolledPast`, — работает при любом
 * контейнере, и ссылки на внутренности ScrollArea не нужны.
 *
 * Читаем координаты раз в кадр (rAF), пишем в MotionValue — без перерисовок
 * React. `enabled=false` (уменьшить движение) — слушателей нет вовсе.
 */
export function useSectionProgress(ref: RefObject<HTMLElement | null>, enabled: boolean): MotionValue<number> {
    const progress = useMotionValue(0);

    useEffect(() => {
        const el = ref.current;
        if (!enabled || !el) return;

        let frame = 0;
        const read = () => {
            frame = 0;
            const rect = el.getBoundingClientRect();
            const span = rect.height || 1;
            progress.set(Math.min(1, Math.max(0, -rect.top / span)));
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(read);
        };

        window.addEventListener("scroll", schedule, {capture: true, passive: true});
        window.addEventListener("resize", schedule, {passive: true});
        read();

        return () => {
            window.removeEventListener("scroll", schedule, {capture: true});
            window.removeEventListener("resize", schedule);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [ref, enabled, progress]);

    return progress;
}
